import { useQuery } from '@apollo/client';
import {
  findClosestAvailableSlotToDate,
  findNextAvailableSlotDate,
} from '@monofront/business/utils';
import { TimeSlotsSidePanel } from '@features/DeliveryBar/TimeSlotsSidePanel';
import { useLocalOrRemoteCustomer } from '@hooks/useLocalOrRemoteCustomer';
import { useMediaQuery } from '@monofront/business/hooks';
import { GET_SLOT_AVAILABILITIES } from '@monofront/business/queries';
import { formatDate } from '@monofront/business/utils';
import DeliveryBar, {
  Arrow,
  Container,
  Label,
  Picto,
  SingleTitle,
  Title,
  TitleContainer,
  VerticalDivider,
} from '@monofront/design-system/components/DeliveryBar';
import CourierDelivery from '@monofront/design-system/visuals/Icons/CourierDelivery';
import DeliveryDate from '@monofront/design-system/visuals/Icons/DeliveryDate';
import GenericDelivery from '@monofront/design-system/visuals/Icons/GenericDelivery';
import PickupDelivery from '@monofront/design-system/visuals/Icons/PickupDelivery';
import StandardDelivery from '@monofront/design-system/visuals/Icons/StandardDelivery';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';
import { trackEvent } from '@monofront/tracking';
import {
  ISlotAvailabilitiesV1QueryResult,
  ShippingMethodTypes,
} from '@monofront/types';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { useDeliveryBar } from '@context/DeliveryBarContext';

const IconByShippingMethod = {
  [ShippingMethodTypes.PICKUP]: PickupDelivery,
  [ShippingMethodTypes.COURIER]: CourierDelivery,
  [ShippingMethodTypes.HOME]: StandardDelivery,
};

export const DeliveryBarCTA = ({ hideIfEmpty }: { hideIfEmpty?: boolean }) => {
  const tDeliveryBarCTA = useTranslations('DeliveryBar.CTA');
  const tDeliveryBarAddressForm = useTranslations('DeliveryBar.AddressForm');
  const [isTimeSlotsSidePanelOpen, setIsTimeSlotsSidePanelOpen] =
    useState(false);
  const { resolvedCustomer: customer, isCustomerLoading } =
    useLocalOrRemoteCustomer();

  const isMobileClient = useMediaQuery(800);

  const {
    address: selectedAddress,
    preferredShippingMethod: selectedShippingMethod,
  } = customer || {};

  const {
    data: slotAvailabilities,
    error,
    loading,
  } = useQuery<ISlotAvailabilitiesV1QueryResult>(GET_SLOT_AVAILABILITIES, {
    ssr: false,
    variables: {
      countryCode: selectedAddress?.country,
      zipCode: selectedAddress?.postalCode,
      isFresh: false,
      isUltraFresh: false,
      carrierMethod: selectedShippingMethod?.carrierId,
    },
    skip:
      !selectedAddress?.country ||
      !selectedAddress?.postalCode ||
      !selectedShippingMethod?.carrierId ||
      selectedShippingMethod?.type !== ShippingMethodTypes.COURIER,
  });

  const {
    setIsSidePanelOpen,
    isSidePanelOpen,
    shippingMethodSelectionContent,
  } = useDeliveryBar();

  const today = new Date();

  const nextAvailableDayWithSlots = useMemo(() => {
    if (!slotAvailabilities?.getSlotAvailabilities?.length) return null;

    return findClosestAvailableSlotToDate(
      slotAvailabilities.getSlotAvailabilities,
      today,
    );
  }, [slotAvailabilities?.getSlotAvailabilities]);

  const isCourier =
    selectedShippingMethod?.type === ShippingMethodTypes.COURIER;
  const shippingMethodType = selectedShippingMethod?.type;

  const nextAvailableSlotDate = nextAvailableDayWithSlots
    ? findNextAvailableSlotDate(nextAvailableDayWithSlots)
    : null;

  const formattedDate = isCourier
    ? nextAvailableSlotDate
      ? formatDate(nextAvailableSlotDate, {
          weekday: 'short',
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })
      : ''
    : shippingMethodSelectionContent?.shippingMethods?.find(
        ({ shippingMethod }) =>
          shippingMethod?.slug === selectedShippingMethod?.type,
      )?.shippingMethod?.data?.subtitle ?? '';

  if (
    isCustomerLoading ||
    (hideIfEmpty && !selectedAddress && !selectedShippingMethod)
  ) {
    return null;
  }

  return (
    <>
      <TimeSlotsSidePanel
        isOpen={isTimeSlotsSidePanelOpen}
        closePanel={() => setIsTimeSlotsSidePanelOpen(false)}
        timeSlots={slotAvailabilities?.getSlotAvailabilities}
        isLoading={loading}
        hasError={Boolean(error)}
      />
      {!selectedAddress || !selectedShippingMethod ? (
        <DeliveryBar
          UNSAFE_className="tw-inline-block"
          onClick={() => {
            setIsSidePanelOpen(true);
            trackEvent({
              type: 'LFDisplayDeliveryBar',
              data: { screen: 'address_bar' },
            });
          }}
        >
          <Picto Component={GenericDelivery} size={IconSizes.md} />
          <SingleTitle UNSAFE_className="tw-ml-2">
            {tDeliveryBarCTA('emptyFormTitle')}
          </SingleTitle>
        </DeliveryBar>
      ) : (
        <DeliveryBar UNSAFE_className="tw-flex-grow">
          <Container
            onClick={() => {
              if (!isSidePanelOpen) {
                setIsSidePanelOpen(true);

                trackEvent({
                  type: 'LFDisplayDeliveryBar',
                  data: { screen: 'shipping_method' },
                });
              }
            }}
          >
            {!isMobileClient && (
              <Picto
                Component={
                  shippingMethodType
                    ? IconByShippingMethod[shippingMethodType]
                    : StandardDelivery
                }
                size={IconSizes.md}
              />
            )}
            {shippingMethodType && (
              <TitleContainer>
                <Title
                  dataTestId={`Deliverybar-configured-shipping-method-title-${shippingMethodType}`}
                >
                  {tDeliveryBarCTA(`shippingTypeTitle.${shippingMethodType}`)}
                </Title>
                <Arrow />
              </TitleContainer>
            )}
            <Label UNSAFE_className="tw-capitalize max-[320px]:tw-w-28">
              {shippingMethodType === ShippingMethodTypes.PICKUP &&
              selectedShippingMethod?.pickupPointLabel
                ? selectedShippingMethod.pickupPointLabel.toLowerCase()
                : tDeliveryBarAddressForm('completeAddress', {
                    street: selectedAddress.street,
                    postcode: selectedAddress.postalCode,
                    city: selectedAddress.city,
                  })}
            </Label>
          </Container>
          <VerticalDivider UNSAFE_className="desktop:tw-mx-4 tw-mx-2" />
          <Container
            onClick={() => {
              setIsTimeSlotsSidePanelOpen(true);
              trackEvent({ type: 'LFZwSlotsOpened' });
            }}
            disabled={!isCourier}
          >
            {!isMobileClient && (
              <Picto Component={DeliveryDate} size={IconSizes.md} />
            )}
            {shippingMethodType && (
              <TitleContainer>
                <Title>
                  {tDeliveryBarCTA(`fromDateTitle.${shippingMethodType}`)}
                </Title>
                {isCourier && <Arrow />}
              </TitleContainer>
            )}
            <Label>{formattedDate}</Label>
          </Container>
        </DeliveryBar>
      )}
    </>
  );
};
